import { useState, useRef } from 'react';

export type UrlType = 'youtube' | 'facebook' | 'instagram' | 'web' | 'any';

const URL_PATTERNS: Record<UrlType, RegExp> = {
  youtube:   /^https?:\/\/(www\.)?(youtube\.com\/(watch\?|shorts\/|@|channel\/|c\/)|youtu\.be\/)/i,
  facebook:  /^https?:\/\/(www\.)?facebook\.com\//i,
  instagram: /^https?:\/\/(www\.)?instagram\.com\//i,
  web:       /^https?:\/\/[^\s/$.?#][^\s]*/i,
  any:       /^https?:\/\/.+/i,
};

const URL_HINTS: Record<UrlType, string> = {
  youtube:   'youtube.com/watch?... vagy youtu.be/...',
  facebook:  'facebook.com/...',
  instagram: 'instagram.com/...',
  web:       'https://pelda.hu',
  any:       'https://...',
};

function validateFormat(value: string, urlType: UrlType): string {
  if (!value) return '';
  if (!/^https?:\/\//i.test(value)) return 'Az URL-nek https:// -vel kell kezdődnie.';
  if (!URL_PATTERNS[urlType].test(value)) return `Érvénytelen formátum. Elvárt: ${URL_HINTS[urlType]}`;
  return '';
}

type ReachState = 'idle' | 'checking' | 'ok' | 'unreachable';

interface LinkFieldProps {
  label: string;
  initialValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  urlType?: UrlType;
  onCheckReachability?: (url: string) => Promise<boolean>;
}

/**
 * URL input with:
 *   - per-type format validation (shown on blur, error blocks save)
 *   - optional async domain reachability check (warning only, never blocks save)
 */
export default function LinkField({
  label,
  initialValue = '',
  onChange,
  placeholder,
  debounceMs = 1500,
  urlType = 'any',
  onCheckReachability,
}: LinkFieldProps) {
  const [value, setValue]         = useState(initialValue);
  const [formatError, setFormatError] = useState('');
  const [touched, setTouched]     = useState(false);
  const [reachState, setReachState] = useState<ReachState>('idle');
  const saveTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reachTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function scheduleCheck(url: string) {
    if (!onCheckReachability || !url) return;
    if (reachTimer.current) clearTimeout(reachTimer.current);
    // Delay reachability check slightly longer than save debounce
    reachTimer.current = setTimeout(async () => {
      setReachState('checking');
      try {
        const ok = await onCheckReachability(url);
        setReachState(ok ? 'ok' : 'unreachable');
      } catch {
        setReachState('unreachable');
      }
    }, debounceMs + 500);
  }

  function handleChange(v: string) {
    setValue(v);
    const err = validateFormat(v, urlType);
    if (touched) setFormatError(err);

    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (reachTimer.current) clearTimeout(reachTimer.current);
    setReachState('idle');

    // Schedule debounced save for valid URL or cleared field (empty string)
    if (!err) {
      saveTimer.current = setTimeout(() => {
        onChange(v);
        if (v) scheduleCheck(v);
      }, debounceMs);
    }
  }

  function handleBlur() {
    setTouched(true);
    const err = validateFormat(value, urlType);
    setFormatError(err);
    // Always cancel pending debounce on blur
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (err) return;
    // Flush save immediately so navigating away doesn't lose the value
    onChange(value);
    if (value && reachState === 'idle') scheduleCheck(value);
  }

  const hasError  = touched && !!formatError;
  const isValid   = touched && !formatError && value.length > 0;

  return (
    <div style={{
      marginBottom: 20, padding: '16px 20px',
      border: `1px solid ${hasError ? '#e74c3c' : 'var(--border)'}`,
      borderRadius: 'var(--radius-sm)',
      transition: 'border-color .15s',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>{label}</div>

      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder ?? 'https://...'}
          style={{
            width: '100%', height: 36, padding: '0 36px 0 10px',
            border: `1px solid ${hasError ? '#e74c3c' : isValid && reachState !== 'unreachable' ? '#27ae60' : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)',
            fontSize: 13, fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
            outline: 'none', transition: 'border-color .15s',
          }}
        />
        {/* Status icon */}
        {isValid && reachState === 'ok' && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#27ae60' }}>ok</span>
        )}
        {reachState === 'checking' && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--muted-foreground)' }}>...</span>
        )}
      </div>

      {/* Format error — blocks save */}
      {hasError && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#e74c3c' }}>{formatError}</div>
      )}

      {/* Reachability warning — informational only */}
      {isValid && reachState === 'unreachable' && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#e67e22', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>!</span>
          <span>A megadott URL nem elérhető. Az érték mentve, de ellenőrizze a linket.</span>
        </div>
      )}
    </div>
  );
}
